/**
 * Matches a Salesforce-shaped id (15-18 alphanumeric characters) that ends at a path/query/hash
 * boundary or the end of the string. Salesforce ids are 15 (case-sensitive) or 18
 * (case-insensitive) characters.
 */
const SALESFORCE_ID_PATTERN = /([a-zA-Z0-9]{15,18})(?:[/?#]|$)/;

/**
 * Parses the Flash Rewards offer-id input field, which accepts either a bare Salesforce offer id
 * or a pasted admin URL (an offer detail page, or a Flash Rewards deep link).
 *
 * Resolution order:
 * 1. A `?offerId=` query param wins when present — this is how the Flash Rewards tab's own deep
 *    links (and ones support hands a customer) carry the id.
 * 2. Otherwise a 15-18 char alphanumeric Salesforce id is extracted from the last path segment of
 *    a pasted URL, or from the raw input itself when it isn't a URL (a bare id behaves like a
 *    single path segment).
 * 3. Whitespace-only input, or input with no id-shaped segment, resolves to `null`.
 *
 * @param input Raw text from the offer-id field.
 * @returns The extracted Salesforce offer id, or `null` when none can be found.
 */
export function parseOfferIdInput(input: string): string | null {
  const trimmed = input.trim();
  if (!trimmed) {
    return null;
  }

  let offerIdParam: string | null = null;
  let candidateSegment = trimmed;

  try {
    const url = new URL(trimmed);
    offerIdParam = url.searchParams.get('offerId');
    const pathSegments = url.pathname.split('/').filter(Boolean);
    candidateSegment = pathSegments[pathSegments.length - 1] ?? trimmed;
  } catch {
    // Not a parseable URL — treat the trimmed input itself as the candidate segment.
  }

  if (offerIdParam) {
    return offerIdParam;
  }

  const match = candidateSegment.match(SALESFORCE_ID_PATTERN);
  return match ? match[1] : null;
}
